import { useEffect, useState } from "react";
import type { Experiment, ExperimentVariant } from "../../client";
import {
  listExperiments,
  createExperiment,
  listVariants,
  createVariant,
  setVariantFavorite,
} from "../../client";
import { ComparisonView } from "./ComparisonView";

type ExperimentPanelProps = {
  selectedJobDir?: string | null;
};

export function ExperimentPanel({ selectedJobDir }: ExperimentPanelProps) {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [selectedExp, setSelectedExp] = useState<Experiment | null>(null);
  const [variants, setVariants] = useState<ExperimentVariant[]>([]);
  const [nameInput, setNameInput] = useState("");
  const [promptOverride, setPromptOverride] = useState("");
  const [modelKey, setModelKey] = useState("");
  const [notes, setNotes] = useState("");
  const [copyArtifacts, setCopyArtifacts] = useState(true);
  const [runJob, setRunJob] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadExperiments = async () => {
    setLoading(true);
    try {
      const data = await listExperiments();
      setExperiments(data);
      setError(null);
      if (data.length && !selectedExp) {
        setSelectedExp(data[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load experiments");
    } finally {
      setLoading(false);
    }
  };

  const loadVariants = async (expId: string) => {
    try {
      const data = await listVariants(expId);
      setVariants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load variants");
    }
  };

  useEffect(() => {
    loadExperiments();
  }, []);

  useEffect(() => {
    if (selectedExp) {
      loadVariants(selectedExp.id);
    } else {
      setVariants([]);
    }
  }, [selectedExp?.id]);

  const handleCreateExperiment = async () => {
    if (!nameInput.trim()) return;
    setLoading(true);
    try {
      const exp = await createExperiment(nameInput.trim());
      setNameInput("");
      await loadExperiments();
      setSelectedExp(exp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create experiment");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVariant = async () => {
    if (!selectedExp) return;
    setLoading(true);
    try {
      await createVariant(selectedExp.id, {
        changes: {},
        isFavorite: false,
        sourceDirectory: selectedJobDir || undefined,
        copyArtifacts,
        promptOverride: promptOverride || undefined,
        modelKey: modelKey || undefined,
        notes: notes || undefined,
        runJob,
      });
      await loadVariants(selectedExp.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create variant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ color: "#e0e0e0", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div>
        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>Experiments</h3>
        <p style={{ margin: 0, color: "#888", fontSize: "0.8rem" }}>
          Create experiments and variants tied to selected jobs to compare outputs.
        </p>
      </div>

      {/* Create experiment */}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          backgroundColor: "#11141c",
          border: "1px solid #1f2430",
          borderRadius: "8px",
          padding: "0.5rem",
        }}
      >
        <input
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          placeholder="Experiment name"
          style={{
            flex: 1,
            backgroundColor: "#0b0d12",
            border: "1px solid #202735",
            borderRadius: "6px",
            color: "#e0e0e0",
            padding: "0.5rem",
            fontSize: "0.85rem",
          }}
        />
        <button
          onClick={handleCreateExperiment}
          disabled={loading}
          style={{
            padding: "0.5rem 0.75rem",
            backgroundColor: "#1976d2",
            border: "none",
            borderRadius: "6px",
            color: "#fff",
            fontSize: "0.8rem",
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Creating..." : "Create"}
        </button>
      </div>

      {/* Experiments list */}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        {experiments.map((exp) => (
          <button
            key={exp.id}
            onClick={() => setSelectedExp(exp)}
            style={{
              padding: "0.5rem 0.75rem",
              borderRadius: "6px",
              border: exp.id === selectedExp?.id ? "1px solid #4a90d9" : "1px solid #1f2430",
              backgroundColor: exp.id === selectedExp?.id ? "#122033" : "#0f141c",
              color: "#e0e0e0",
              cursor: "pointer",
              fontSize: "0.8rem",
            }}
          >
            {exp.name}
          </button>
        ))}
      </div>

      {/* Variants */}
      {selectedExp && (
        <div
          style={{
            backgroundColor: "#0f1115",
            border: "1px solid #1f2430",
            borderRadius: "8px",
            padding: "0.75rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 style={{ margin: 0, fontSize: "0.95rem" }}>{selectedExp.name} variants</h4>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.75rem", color: "#c0c0c0" }}>
                <input type="checkbox" checked={copyArtifacts} onChange={(e) => setCopyArtifacts(e.target.checked)} />
                Copy artifacts
              </label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.75rem", color: "#c0c0c0" }}>
                <input type="checkbox" checked={runJob} onChange={(e) => setRunJob(e.target.checked)} />
                Launch job
              </label>
              <button
                onClick={handleCreateVariant}
                disabled={loading}
                style={{
                  padding: "0.4rem 0.7rem",
                  backgroundColor: "#2e7d32",
                  border: "none",
                  borderRadius: "6px",
                  color: "#fff",
                  fontSize: "0.75rem",
                  cursor: loading ? "wait" : "pointer",
                }}
              >
                {loading ? "Adding…" : "Add variant"}
              </button>
            </div>
          </div>

          {/* Prompt/model overrides */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
              gap: "0.5rem",
              marginTop: "0.5rem",
            }}
          >
            <textarea
              value={promptOverride}
              onChange={(e) => setPromptOverride(e.target.value)}
              placeholder="Prompt override (optional)"
              rows={2}
              style={{
                width: "100%",
                backgroundColor: "#0b0d12",
                border: "1px solid #202735",
                borderRadius: "6px",
                color: "#e0e0e0",
                padding: "0.5rem",
                fontSize: "0.8rem",
              }}
            />
            <input
              value={modelKey}
              onChange={(e) => setModelKey(e.target.value)}
              placeholder="Model key override (optional)"
              style={{
                width: "100%",
                backgroundColor: "#0b0d12",
                border: "1px solid #202735",
                borderRadius: "6px",
                color: "#e0e0e0",
                padding: "0.5rem",
                fontSize: "0.8rem",
              }}
            />
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes"
              style={{
                width: "100%",
                backgroundColor: "#0b0d12",
                border: "1px solid #202735",
                borderRadius: "6px",
                color: "#e0e0e0",
                padding: "0.5rem",
                fontSize: "0.8rem",
              }}
            />
          </div>

          {variants.length === 0 ? (
            <p style={{ color: "#888", fontSize: "0.8rem", marginTop: "0.5rem" }}>No variants yet.</p>
          ) : (
            <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {variants.map((v) => (
                <VariantRow
                  key={v.id}
                  variant={v}
                  onToggleFavorite={async (fav) => {
                    try {
                      await setVariantFavorite(selectedExp.id, v.id, fav);
                      await loadVariants(selectedExp.id);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Failed to update favorite");
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {selectedExp && (
        <div
          style={{
            backgroundColor: "#0f1115",
            border: "1px solid #1f2430",
            borderRadius: "8px",
            padding: "0.75rem",
          }}
        >
          <ComparisonView experimentId={selectedExp.id} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            color: "#ef5350",
            fontSize: "0.8rem",
            padding: "0.5rem",
            backgroundColor: "#1a0b0b",
            border: "1px solid #4a1313",
            borderRadius: "6px",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function VariantRow({
  variant,
  onToggleFavorite,
}: {
  variant: ExperimentVariant;
  onToggleFavorite: (isFavorite: boolean) => void;
}) {
  const changes = variant.changes || {};
  const meta = ["model_key", "prompt_override", "variant_directory", "source_directory", "notes"]
    .map((k) => changes[k])
    .filter(Boolean)
    .map(String);

  return (
    <div
      style={{
        padding: "0.55rem",
        borderRadius: "6px",
        border: "1px solid #1f2430",
        backgroundColor: "#0c0f16",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: "0.8rem",
        gap: "0.75rem",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>Variant {variant.id.slice(0, 8)}</div>
        <div style={{ color: "#888" }}>Job: {variant.job_id || "pending"}</div>
        {meta.length > 0 && (
          <div style={{ color: "#999", fontSize: "0.75rem", marginTop: "0.25rem" }}>
            {meta.map((m) => (
              <div key={m}>{m}</div>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => onToggleFavorite(!variant.is_favorite)}
        style={{
          background: "none",
          border: "none",
          color: variant.is_favorite ? "#ffd54f" : "#666",
          fontSize: "1rem",
          cursor: "pointer",
        }}
        title={variant.is_favorite ? "Unfavorite" : "Favorite"}
      >
        ★
      </button>
    </div>
  );
}
